/** A single padded, rounded cutout in viewport coordinates. */
export interface GuideHole {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
}

/**
 * Dimming overlay with one or more spotlight cutouts. Rather than dimming
 * around the bounding box of the target PLUS every popover it opened (which
 * balloons into one huge bright rectangle when a dropdown is far from its
 * trigger), each element gets its OWN hole and the dim between them stays dark —
 * the cutouts are STITCHED, not unioned.
 *
 * The dim is an SVG rect masked by a white full-screen rect minus a black
 * rounded rect per hole — purely visual (pointer-events-none). Modal click
 * blocking lives in GuideOverlay as an element-aware event handler (a geometric
 * shield can't tell a legit portaled calendar from the dim). The violet target
 * ring is drawn separately (GuideSpotlight).
 */
export function GuideBackdrop({
  holes,
  reducedMotion,
}: Readonly<{
  holes: readonly GuideHole[];
  reducedMotion: boolean;
}>) {
  const vw = typeof window === "undefined" ? 0 : window.innerWidth;
  const vh = typeof window === "undefined" ? 0 : window.innerHeight;
  return (
    <svg
      data-testid="guide-backdrop"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[7990]"
      width={vw}
      height={vh}
      style={{
        transition: reducedMotion ? undefined : "opacity 180ms ease-out",
      }}
    >
      <defs>
        <mask id="guide-hole-mask">
          <rect x={0} y={0} width={vw} height={vh} fill="white" />
          {holes.map((hole) => {
            // Clamp UNIFORMLY to the smaller half-side. SVG otherwise clamps
            // rx→w/2 and ry→h/2 independently, so a wide pill (rounded-full)
            // renders as an ellipse instead of the stadium the CSS ring draws
            // — the two then disagree at the corners.
            const r = Math.min(hole.r, hole.w / 2, hole.h / 2);
            return (
              <rect
                key={`${hole.x},${hole.y},${hole.w},${hole.h}`}
                x={hole.x}
                y={hole.y}
                width={hole.w}
                height={hole.h}
                rx={r}
                ry={r}
                fill="black"
              />
            );
          })}
        </mask>
      </defs>
      <rect
        x={0}
        y={0}
        width={vw}
        height={vh}
        fill="rgba(15, 23, 42, 0.5)"
        mask="url(#guide-hole-mask)"
      />
    </svg>
  );
}
